import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Backdrop, Panel, Header, Footer, Field } from "./DepositModal";

export default function WithdrawModal({ initialAsset, onClose }) {
  const [assets, setAssets] = useState(null);
  const [balances, setBalances] = useState({});
  const [asset, setAsset] = useState(initialAsset || null);
  const [network, setNetwork] = useState(null);
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [memo, setMemo] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [travelRule, setTravelRule] = useState({ configured: false, vasps: [] });
  const [beneficiaryVaspId, setBeneficiaryVaspId] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/api/wallet/assets"),
      api.get("/api/wallet/balances"),
      api.get("/api/wallet/whitelist"),
      api.get("/api/verifyvasp/status").catch(() => ({ data: { configured: false } })),
    ]).then(async ([a, b, w, vv]) => {
      const real = a.data.assets.filter((x) => !x.is_fiat);
      setAssets(real);
      const map = {};
      (b.data.balances || []).forEach((bal) => { map[bal.asset] = bal; });
      setBalances(map);
      setWhitelist(w.data.whitelist || []);
      if (!asset && real.length) setAsset(initialAsset || real[0].symbol);
      const configured = Boolean(vv?.data?.configured);
      if (configured) {
        try {
          const { data } = await api.get("/api/verifyvasp/vasps");
          setTravelRule({ configured: true, vasps: data.vasps || [] });
        } catch {
          setTravelRule({ configured: true, vasps: [] });
        }
      } else {
        setTravelRule({ configured: false, vasps: [] });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!asset || !assets) return;
    const a = assets.find((x) => x.symbol === asset);
    if (a && a.networks.length) setNetwork(a.networks[0].network);
  }, [asset, assets]);

  const aSel = assets?.find((x) => x.symbol === asset);
  const nSel = aSel?.networks.find((n) => n.network === network);
  const bal = balances[asset];
  const fee = nSel?.withdraw_fee || "0";
  const minAmt = nSel?.min_withdraw || "0";
  const total = (Number(amount || 0) + Number(fee)).toFixed(8).replace(/\.?0+$/, "");
  const insufficient = bal && Number(total) > Number(bal.available);

  // Whitelist checking
  const matchedWhitelistEntry = whitelist.find(
    (w) => w.asset === asset && w.network === network && w.address === address
  );
  
  const withdrawWhitelistStatus = () => {
    if (!matchedWhitelistEntry) return "none";
    const status = matchedWhitelistEntry.status
      || (matchedWhitelistEntry.verified ? "approved" : "pending_admin");
    if (status === "pending_admin") return "pending";
    if (status === "rejected") return "rejected";
    if (matchedWhitelistEntry.is_usable) return "active";
    if (matchedWhitelistEntry.locked_until) {
      const until = new Date(matchedWhitelistEntry.locked_until);
      if (until > new Date()) return "cooling";
    }
    return status === "approved" ? "active" : "invalid";
  };

  const wlStatus = withdrawWhitelistStatus();
  const isAddressWhitelisted = wlStatus === "active" || wlStatus === "cooling";
  const isAddressUsable = wlStatus === "active";

  const isAddressInCooling = () => wlStatus === "cooling";

  const getCoolingHoursLeft = () => {
    if (!matchedWhitelistEntry?.locked_until) return 0;
    const now = new Date();
    const until = new Date(matchedWhitelistEntry.locked_until);
    const diffMs = until - now;
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60)));
  };

  const filteredWhitelist = whitelist.filter(
    (w) => w.asset === asset && w.network === network && (w.is_usable || w.status === "approved"),
  );

  const setMaxAmount = () => {
    if (!bal) return;
    const max = Math.max(0, Number(bal.available) - Number(fee));
    setAmount(String(max));
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      if (travelRule.configured && !beneficiaryVaspId) {
        setError("Select the destination exchange (Travel Rule VASP).");
        setBusy(false);
        return;
      }
      const r = await api.post("/api/wallet/withdraw", {
        asset,
        network,
        amount,
        address,
        memo: memo || undefined,
        totp_code: totp,
        ...(travelRule.configured
          ? {
              beneficiary_vasp_id: beneficiaryVaspId,
              beneficiary_name: beneficiaryName || undefined,
              beneficiary_vasp_name:
                travelRule.vasps.find((v) => String(v.vaspId) === String(beneficiaryVaspId))
                  ?.vaspName || undefined,
            }
          : {}),
      });
      setSuccess(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <Backdrop onClose={() => onClose(true)}>
        <Panel>
          <Header title="Withdrawal Submitted" onClose={() => onClose(true)} testId="withdraw-close" />
          <div className="p-6 space-y-4 text-[13px]">
            <div className="rounded-lg bg-up/10 text-up p-4">
              <div className="font-semibold">Your withdrawal request is in the admin approval queue.</div>
              <div className="mt-1 text-[12px]">Reference: <b className="font-mono">{success.tx_id}</b></div>
              <div className="text-[12px] mt-1">Track its progress in the History tab below.</div>
            </div>
          </div>
          <Footer>
            <button
              onClick={() => onClose(true)}
              className="px-5 py-2 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold"
              data-testid="withdraw-success-done"
            >
              Done
            </button>
          </Footer>
        </Panel>
      </Backdrop>
    );
  }

  if (confirming) {
    return (
      <Backdrop onClose={() => setConfirming(false)}>
        <Panel>
          <Header title="Confirm Withdrawal" onClose={() => setConfirming(false)} testId="withdraw-cancel-confirm" />
          <div className="p-6 space-y-3 text-[13px]">
            <Row label="Asset" value={`${asset} on ${network}`} />
            <Row label="Amount" value={`${amount} ${asset}`} />
            <Row label="Network Fee" value={`${fee} ${asset}`} />
            <Row label="Total Debit" value={`${total} ${asset}`} bold />
            <Row label="Destination" value={address} mono />
            {memo && <Row label="Memo / Tag" value={memo} mono />}
            <div className="rounded-lg bg-warning/10 text-warning p-3 text-[12px]">
              ⚠ Crypto withdrawals are <b>irreversible</b>. Verify every detail before confirming.
            </div>
            <Field label="2FA Code">
              <input
                type="text"
                inputMode="numeric"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="Enter 6-digit code"
                className="w-full px-3 py-2.5 rounded-lg border border-border text-[14px] font-mono tracking-widest text-center"
                data-testid="withdraw-totp"
              />
            </Field>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-down/10 text-down text-[12px]">
                <AlertCircle size={14} className="mt-0.5" /> {error}
              </div>
            )}
          </div>
          <Footer>
            <button
              onClick={() => setConfirming(false)}
              className="px-4 py-2 rounded-lg border border-border text-foreground text-[13px] font-semibold"
              data-testid="withdraw-back"
            >
              Back
            </button>
            <button
              onClick={submit}
              disabled={busy || totp.length < 6}
              className="px-5 py-2 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="withdraw-confirm-submit"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Confirm Withdrawal
            </button>
          </Footer>
        </Panel>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={() => onClose(false)}>
      <Panel>
        <Header title="Withdraw Crypto" onClose={() => onClose(false)} testId="withdraw-close" />
        <div className="p-6 space-y-4">
          <Field label="Asset">
            <select
              value={asset || ""}
              onChange={(e) => setAsset(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary text-[14px]"
              data-testid="withdraw-asset-select"
            >
              {(assets || []).map((a) => (
                <option key={a.symbol} value={a.symbol}>
                  {a.symbol} — {a.name}
                </option>
              ))}
            </select>
          </Field>

          {aSel?.networks.length > 0 && (
            <Field label="Network">
              <div className="grid grid-cols-3 gap-2">
                {aSel.networks.map((n) => (
                  <button
                    key={n.network}
                    onClick={() => setNetwork(n.network)}
                    className={`px-3 py-2 rounded-lg border text-[13px] font-semibold ${
                      network === n.network
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-foreground hover:bg-surface-2"
                    }`}
                    data-testid={`withdraw-network-${n.network}`}
                  >
                    {n.network}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[12px] font-semibold text-foreground uppercase tracking-wider">Destination Address</label>
              {filteredWhitelist.length > 0 && (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const entry = filteredWhitelist.find((x) => x.id === e.target.value);
                      if (entry) {
                        setAddress(entry.address);
                        if (entry.memo) setMemo(entry.memo);
                      }
                    }
                  }}
                  className="text-[11px] text-primary bg-transparent border-none cursor-pointer focus:outline-none font-semibold"
                  data-testid="withdraw-pick-whitelist"
                  defaultValue=""
                >
                  <option value="" disabled>— Pick from Whitelist —</option>
                  {filteredWhitelist.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.label} ({w.address.slice(0, 6)}...{w.address.slice(-4)})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value.trim())}
              placeholder={`${network || "Network"} address`}
              className="w-full px-3 py-2.5 rounded-lg border border-border text-[13px] font-mono"
              data-testid="withdraw-address"
            />
          </div>

          {/* Whitelist status verification */}
          {address && (
            <div className="text-[12px] p-2.5 rounded-md border border-border flex items-center justify-between bg-cm-surface/50">
              <span className="text-muted-foreground font-medium">Whitelist Status:</span>
              {wlStatus === "active" ? (
                <span className="text-up font-semibold flex items-center gap-1" data-testid="withdraw-whitelist-status-active">
                  <Check size={12} /> Active ✓
                </span>
              ) : wlStatus === "cooling" ? (
                <span className="text-warning font-semibold flex items-center gap-1" data-testid="withdraw-whitelist-status-cooling">
                  <Clock size={12} /> Cooling ({getCoolingHoursLeft()}h remaining)
                </span>
              ) : wlStatus === "pending" ? (
                <span className="text-warning font-semibold flex items-center gap-1" data-testid="withdraw-whitelist-status-pending">
                  <Clock size={12} /> Pending admin approval
                </span>
              ) : wlStatus === "rejected" ? (
                <span className="text-down font-semibold flex items-center gap-1" data-testid="withdraw-whitelist-status-rejected">
                  <AlertCircle size={12} /> Rejected
                </span>
              ) : (
                <span className="text-down font-semibold flex items-center gap-1" data-testid="withdraw-whitelist-status-invalid">
                  <AlertCircle size={12} /> Not Whitelisted ⚠
                </span>
              )}
            </div>
          )}

          {address && wlStatus === "none" && (
            <div className="p-3 rounded-lg bg-down/10 border border-down/30 text-down text-[12px]">
              ⚠ <b>Address Enforced:</b> Withdrawals are restricted to admin-approved whitelisted addresses only.
            </div>
          )}
          {address && wlStatus === "pending" && (
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 text-warning text-[12px]">
              This address is awaiting admin approval before it can be used for withdrawals.
            </div>
          )}
          {isAddressInCooling() && (
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 text-warning text-[12px]">
              ⚠ <b>Cooling Period:</b> This address is in its 24-hour cooling period and cannot receive withdrawals yet.
            </div>
          )}

          {travelRule.configured ? (
            <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div className="text-[12px] font-semibold text-foreground">Travel Rule (VerifyVASP)</div>
              <label className="block text-[11px] text-muted-foreground font-medium">
                Destination exchange / VASP
                <select
                  value={beneficiaryVaspId}
                  onChange={(e) => setBeneficiaryVaspId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary text-[13px]"
                  data-testid="withdraw-beneficiary-vasp"
                >
                  <option value="">Select VASP…</option>
                  {travelRule.vasps.map((v) => (
                    <option key={v.vaspId} value={v.vaspId}>
                      {v.vaspName}
                      {v.countryCode ? ` (${v.countryCode})` : ""}
                      {v.health === "DOWN" ? " — offline" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] text-muted-foreground font-medium">
                Beneficiary name (optional)
                <input
                  type="text"
                  value={beneficiaryName}
                  onChange={(e) => setBeneficiaryName(e.target.value)}
                  placeholder="Legal name on destination account"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface-2 outline-none focus:border-primary text-[13px]"
                  data-testid="withdraw-beneficiary-name"
                />
              </label>
            </div>
          ) : null}

          {aSel?.needs_memo && (
            <Field label="Destination Tag / Memo (REQUIRED)">
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value.trim())}
                placeholder="e.g., 12345678"
                className="w-full px-3 py-2.5 rounded-lg border border-warning/30 bg-warning/10 text-[13px] font-mono"
                data-testid="withdraw-memo"
              />
            </Field>
          )}

          <Field label={
            <span className="flex items-center justify-between">
              <span>Amount</span>
              <span className="font-normal normal-case text-[11px] text-muted-foreground">
                Available: {bal ? Number(bal.available).toFixed(6) : "0"} {asset}
              </span>
            </span>
          }>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`Min ${minAmt}`}
                className="flex-1 px-3 py-2.5 rounded-lg border border-border text-[14px]"
                data-testid="withdraw-amount"
              />
              <button
                onClick={setMaxAmount}
                className="px-3 py-2.5 rounded-lg bg-surface-2 text-foreground text-[12px] font-semibold"
                data-testid="withdraw-max"
              >
                Max
              </button>
            </div>
          </Field>

          <div className="rounded-lg bg-surface-2 p-3 space-y-1 text-[12px]">
            <Row label="Network Fee" value={`${fee} ${asset}`} />
            <Row label="You will receive" value={`${amount || 0} ${asset}`} />
            <Row label="Total Debit" value={`${total} ${asset}`} bold />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-down/10 text-down text-[12px]">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          )}
        </div>
        <Footer>
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg border border-border text-foreground text-[13px] font-semibold"
            data-testid="withdraw-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => setConfirming(true)}
            disabled={!asset || !network || !address || !amount || Number(amount) < Number(minAmt) || insufficient || !isAddressUsable}
            className="px-5 py-2 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold disabled:opacity-50"
            data-testid="withdraw-review"
          >
            Review Withdrawal
          </button>
        </Footer>
      </Panel>
    </Backdrop>
  );
}


function Row({ label, value, bold, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? "font-semibold text-foreground" : "text-foreground"} ${mono ? "font-mono text-[11px] truncate max-w-[280px]" : ""}`}>
        {value}
      </span>
    </div>
  );
}
